import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { Loading02 } from '@untitledui/icons/Loading02';
import { useCallback, useState } from 'react';
import { z } from 'zod';

import type { ValidatedLeadDto } from '@/api/core-api';

import { Button } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { notify } from '@/components/base/notifications/notifications';
import { useRejectLead } from '@/hooks/api/use-reject-lead';
import { useValidateLead } from '@/hooks/api/use-validate-lead';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { LeadSuccessStep } from '@/routes/participants/-components/lead-success-step';

import { requireParticipantSession } from './-utils/participant-route-guards';

export const Route = createFileRoute('/participants/lead')({
  beforeLoad: ({ context, location }) => {
    const { user, profile, env } = context;

    requireParticipantSession(user, env, location);

    if (!profile) {
      throw new Error('Failed to load profile');
    }

    if (!profile.isOnboardingComplete) {
      throw redirect({ to: '/participants/onboarding' });
    }

    if (!profile.isPendingLead) {
      throw redirect({ to: '/participants/projects' });
    }
    return { user, profile };
  },
  component: LeadPage,
});

type Step = 'form' | 'loading' | 'success' | 'error';

// Minimum time to spend in the loading step so the "fetching your details"
// moment feels real even when the API responds instantly.
const MIN_LOADING_MS = 2000;

function LeadPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  const [step, setStep] = useState<Step>('form');
  const [validatedLead, setValidatedLead] = useState<
    (ValidatedLeadDto & { email: string }) | null
  >(null);

  const validateLeadMutation = useValidateLead();
  const rejectLeadMutation = useRejectLead();

  const verifySchema = z.object({
    email: z.email(t`Enter a valid email address`).min(5, t`Email is required`),
  });

  const goToProjects = useCallback(() => {
    navigate({
      to: '/participants/projects',
      reloadDocument: true,
    });
  }, [navigate]);

  const handleRejectLead = async () => {
    await rejectLeadMutation.mutateAsync(
      { userId: user.id },
      {
        onSuccess: goToProjects,
        onError: () => {
          notify({
            title: t`Something went wrong, please try again.`,
            color: 'error',
          });
        },
      },
    );
  };

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onSubmit: verifySchema, onChange: verifySchema },
    onSubmit: async ({ value }) => {
      setStep('loading');

      const minDelay = new Promise((resolve) =>
        setTimeout(resolve, MIN_LOADING_MS),
      );

      try {
        const [lead] = await Promise.all([
          validateLeadMutation.mutateAsync({
            userId: user.id,
            email: value.email,
          }),
          minDelay,
        ]);

        setValidatedLead({ ...lead, email: value.email });
        setStep('success');
      } catch {
        await minDelay;
        setStep('error');
      }
    },
  });

  if (step === 'success' && validatedLead) {
    return (
      <AuthLayout>
        <LeadSuccessStep
          email={validatedLead.email}
          lead={validatedLead}
          userId={user.id}
          onContinue={goToProjects}
        />
      </AuthLayout>
    );
  }

  if (step === 'loading' || step === 'error') {
    return (
      <AuthLayout>
        <ResultStep step={step} onContinue={goToProjects} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        {/* Header */}
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Fast track your sign-up</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              Enter your work email to pre-fill your professional profile.
            </Trans>
          </p>
        </div>

        {/* Form fields */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <FormInput
              type="email"
              field="email"
              size="md"
              form={form}
              label={t`Work email`}
              placeholder={t`name@example.com`}
              isRequired
            />
          </div>

          {/* Submit button */}
          <form.Subscribe
            selector={(state) => ({
              isSubmitting: state.isSubmitting,
              isFormValid: state.isValid,
              isDefaultValue: state.isDefaultValue,
            })}
            children={({ isSubmitting, isFormValid, isDefaultValue }) => (
              <>
                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  isLoading={isSubmitting}
                  isDisabled={
                    isSubmitting ||
                    !isFormValid ||
                    isDefaultValue ||
                    rejectLeadMutation.isPending
                  }
                  className="w-full"
                >
                  <Trans>Pre-fill my profile</Trans>
                </Button>

                <Button
                  color="tertiary"
                  size="lg"
                  isDisabled={isSubmitting || rejectLeadMutation.isPending}
                  className="w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    void handleRejectLead();
                  }}
                >
                  <Trans>No thanks</Trans>
                </Button>
              </>
            )}
          />
        </div>
      </form>
    </AuthLayout>
  );
}

function ResultStep({
  step,
  onContinue,
}: {
  step: 'loading' | 'error';
  onContinue: () => void;
}) {
  return (
    <div className="flex flex-col items-center text-center">
      <DecorativeCircles>
        {step === 'loading' && (
          <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-brand-solid">
            <Loading02 className="size-7 animate-spin text-white" />
          </div>
        )}

        {step === 'error' && (
          <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-error-solid">
            <AlertCircle className="size-7 text-white" />
          </div>
        )}
      </DecorativeCircles>

      <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
        {step === 'loading' && <Trans>Fetching your details...</Trans>}
        {step === 'error' && <Trans>We couldn&apos;t find your details</Trans>}
      </h1>

      <p className="z-10 mt-3 text-md text-primary">
        {step === 'loading' && (
          <Trans>Hang tight while we look up your profile.</Trans>
        )}
        {step === 'error' && (
          <Trans>
            We couldn&apos;t pre-fill your profile from that email. You can
            always update your profile later from the settings page.
          </Trans>
        )}
      </p>

      {step === 'error' && (
        <div className="z-10 mt-8 flex w-full max-w-xs flex-col gap-3">
          <Button
            color="primary"
            size="lg"
            className="w-full"
            onClick={onContinue}
          >
            <Trans>Continue to projects</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}
